import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { getClusterings } from '@/api/sales/clustering';
import { getDeliveryLocationGroups } from '@/api/sales/deliveryLocationGroup';
import { getOrganisationMaxLevel } from '@/api/core/organisation';
import { getOrganisationSharings } from '@/api/core/organisationSharing';
import { useTranslator } from '@/composables/useTranslator';
import { log } from '@/utils/core/vueLogger.js';
import { buildOrganisationById, buildOrgSharingExpansionMap, buildUserOrgStrictScope, expandOrganisationPerimeter, isAncestorOf } from '@/utils/domain/organisationScope';

const t = useTranslator();

const SPS_REFERENCE = 'SPS';

/**
 * SPS ("Score Point de vente") is a per-delivery-location score that, unlike the ratio KPIs handled by
 * kpiService, is NOT computed from statements: it comes pre-computed in the SFA_LATEST_DELIVERYLOCATIONS_KPIS
 * snapshot and is read through the legacy sfaHelper.kpi.getCustomDeliveryLocationKPIs bridge (same call the
 * legacy widget-sps.js makes). It has no objective/tolerance, so its gauge/bar stays neutral (blue like clusters).
 * This service mirrors kpiService's role for that snapshot: overview (global average), per-cluster scores
 * (sorted high→low) and per-DL detail rows.
 */
class SpsService {
	_getEcosCollection(collection) {
		if (typeof collection === 'function') {
			return collection() || [];
		}

		return Array.isArray(collection) ? collection : [];
	}

	_getSpsKpi() {
		const ecosVM = iframeDataBridge.getEcosVM();
		const kpis = this._getEcosCollection(ecosVM?.kpis);
		return kpis.find((kpi) => kpi?.reference === SPS_REFERENCE) || null;
	}

	/**
	 * Whether the current client/context exposes the SPS KPI at all. Used to gate the widget so clients
	 * without SPS never see an empty gauge.
	 */
	isAvailable() {
		return !!this._getSpsKpi();
	}

	/**
	 * Filters the user's delivery locations by the SPS-relevant filters (Organisations — resolved through
	 * the shared expandOrganisationPerimeter(), same as kpiService — Accounts, Channels, Delivery Location
	 * Type, and an optional single cluster). No product/statement dimension applies to a per-store score.
	 * @private
	 */
	async _getFilteredDeliveryLocations(filters = {}) {
		const ecosVM = iframeDataBridge.getEcosVM();
		const context = iframeDataBridge.getContext();
		const organisationById = buildOrganisationById(this._getEcosCollection(ecosVM?.organisations));

		const rawDeliveryLocations = context?.filterByOwnDeliveryLocations ? ecosVM?.ownDeliveryLocations : ecosVM?.deliveryLocations;
		const allDeliveryLocations = (rawDeliveryLocations || []).filter((dl) => dl.accountId > 0);

		const accounts = filters.accounts || [];
		const channels = filters.channels || [];
		const deliveryLocationTypes = filters.deliveryLocationTypes || filters.dlts || [];
		const organisations = filters.organisations || [];
		const clusterId = filters.clusterId ?? null;
		const expandedOrgIds = organisations.length ? expandOrganisationPerimeter(organisations, organisationById, buildOrgSharingExpansionMap(await getOrganisationSharings()), filters.excludeSharedOrganisations) : null;
		const userOrgStrictScope = filters.excludeSharedOrganisations ? buildUserOrgStrictScope(Number(context?.user?.organisationId || 0), organisationById) : null;

		return allDeliveryLocations.filter((dl) => {
			if (userOrgStrictScope) {
				const assignedOrganisationIds = (Array.isArray(dl.organisations) ? dl.organisations : []).map(Number);
				if (assignedOrganisationIds.length > 0 && !assignedOrganisationIds.some((organisationId) => userOrgStrictScope.has(organisationId))) return false;
			}

			if (deliveryLocationTypes.length && !deliveryLocationTypes.includes(dl.deliveryLocationTypeId)) return false;
			if (accounts.length && !accounts.includes(dl.accountId)) return false;
			if (channels.length && !channels.includes(dl.channelId)) return false;

			if (expandedOrgIds) {
				const deliveryLocationOrganisations = (Array.isArray(dl.organisations) ? dl.organisations : []).map(Number);
				if (!deliveryLocationOrganisations.some((organisationId) => expandedOrgIds.has(organisationId))) return false;
			}

			if (clusterId !== null && clusterId !== undefined) {
				if (dl.clusterId == null || Number(dl.clusterId) !== Number(clusterId)) return false;
			}

			return true;
		});
	}

	/**
	 * Reads the pre-computed SPS value (+ diff vs previous visit) for each DL through the legacy bridge.
	 * Mirrors widget-sps.js: ask for the last 2 items so the bridge can derive diffV1, keep only the
	 * `default` source SPS entry per DL.
	 * @private
	 * @returns {Promise<Map<number, {value: number, diffV1: number}>>}
	 */
	_fetchSpsByDeliveryLocation(deliveryLocationIds) {
		return new Promise((resolve) => {
			const sfaHelper = iframeDataBridge.getSfaHelper();
			const spsKpi = this._getSpsKpi();

			if (!sfaHelper?.kpi?.getCustomDeliveryLocationKPIs || !spsKpi || !deliveryLocationIds.length) {
				resolve(new Map());
				return;
			}

			try {
				sfaHelper.kpi.getCustomDeliveryLocationKPIs(
					(datas) => {
						const spsByDeliveryLocation = new Map();
						(Array.isArray(datas) ? datas : []).forEach((item) => {
							if (item && item.reference === SPS_REFERENCE && item.source === 'default') {
								spsByDeliveryLocation.set(item.dlId, { value: Number(item.value) || 0, diffV1: Number(item.diffV1) || 0 });
							}
						});
						resolve(spsByDeliveryLocation);
					},
					[spsKpi.id],
					deliveryLocationIds,
					null,
					new Date(),
					2
				);
			} catch (error) {
				log('error', 'SpsService', `Error fetching SPS data: ${error.message}`);
				resolve(new Map());
			}
		});
	}

	/**
	 * Filtered delivery locations paired with their SPS value/diff. Only DLs that actually have an SPS
	 * record are kept (same as the legacy widget), so unscored stores never dilute the averages.
	 * @private
	 */
	async _buildDeliveryLocationEntries(filters = {}) {
		const deliveryLocations = await this._getFilteredDeliveryLocations(filters);
		if (!deliveryLocations.length) {
			return [];
		}

		const spsByDeliveryLocation = await this._fetchSpsByDeliveryLocation(deliveryLocations.map((dl) => dl.id));

		const entries = [];
		deliveryLocations.forEach((deliveryLocation) => {
			const sps = spsByDeliveryLocation.get(deliveryLocation.id);
			if (!sps) return;
			entries.push({ deliveryLocation, value: sps.value, diffV1: sps.diffV1 });
		});
		return entries;
	}

	_average(values) {
		if (!values.length) return 0;
		return values.reduce((memo, value) => memo + value, 0) / values.length;
	}

	_round1(value) {
		return Math.round((Number(value) || 0) * 10) / 10;
	}

	_formatDifference(value) {
		return value > 0 ? `+${value}` : `${value}`;
	}

	_differenceClassColor(value) {
		if (value > 0) return 'positive';
		if (value < 0) return 'negative';
		return '';
	}

	async _getClusterNameById() {
		try {
			const clusterings = await getClusterings();
			return new Map((Array.isArray(clusterings) ? clusterings : []).map((cluster) => [Number(cluster.id), cluster.name || cluster.label || `${t('Cluster')} ${cluster.id}`]));
		} catch (error) {
			log('warn', 'SpsService', `Could not load clusterings: ${error.message}`);
			return new Map();
		}
	}

	async _getLocationGroupNameById() {
		try {
			const locationGroups = await getDeliveryLocationGroups();
			return new Map((Array.isArray(locationGroups) ? locationGroups : []).map((group) => [Number(group.id), group.name || group.label || '']));
		} catch (error) {
			log('warn', 'SpsService', `Could not load delivery location groups: ${error.message}`);
			return new Map();
		}
	}

	/**
	 * Splits a delivery location's organisations into one path per leaf org (mirrors kpiService), so a
	 * store attached to several non-overlapping orgs gets one grouped row per org branch instead of just one.
	 * @private
	 */
	_buildOrganisationPaths(organisations, organisationById, organisationMaxLevel) {
		if (!organisations || organisations.length === 0) {
			return [{}];
		}

		const leafOrgs = organisations.filter((org) => !organisations.some((other) => Number(other.id) !== Number(org.id) && isAncestorOf(Number(org.id), other, organisationById)));
		const targetOrgs = leafOrgs.length > 0 ? leafOrgs : [organisations[0]];

		return targetOrgs.map((org) => {
			const levels = {};
			const maxLvl = Math.min(org.level, organisationMaxLevel);
			levels[`organisationLevel${maxLvl}`] = org.name || org.label || org.reference || '';
			let current = org;
			for (let level = maxLvl - 1; level >= 1; level--) {
				if (!current?.parentId) break;
				const parent = organisationById.get(Number(current.parentId));
				levels[`organisationLevel${level}`] = parent ? parent.name || parent.label || parent.reference || '' : '';
				current = parent;
			}
			return levels;
		});
	}

	/**
	 * Global SPS averaged over every scored store the filters keep, shaped like a Kpi.vue staticData blob
	 * (neutral/blue: no objective, color stays primary).
	 * @private
	 */
	_buildOverview(entries) {
		const value = this._round1(this._average(entries.map((entry) => entry.value)));
		const difference = this._round1(this._average(entries.map((entry) => entry.diffV1)));

		return {
			value,
			percentage: value,
			formattedValue: `${value}`,
			difference,
			formattedDifference: this._formatDifference(difference),
			classColor: this._differenceClassColor(difference),
			color: 'primary',
			objective: '',
			deliveryLocationCount: entries.length,
		};
	}

	/**
	 * Per-cluster SPS (average score + average diff), sorted from highest to lowest score.
	 * Stores without a cluster are ignored, matching the legacy widget which iterates clusterings.
	 * @private
	 */
	_buildClusters(entries, clusterNameById) {
		const entriesByCluster = new Map();
		entries.forEach((entry) => {
			const clusterId = Number(entry.deliveryLocation.clusterId);
			if (!Number.isInteger(clusterId) || clusterId <= 0) return;
			if (!entriesByCluster.has(clusterId)) entriesByCluster.set(clusterId, []);
			entriesByCluster.get(clusterId).push(entry);
		});

		return Array.from(entriesByCluster.entries())
			.map(([clusterId, clusterEntries]) => {
				const value = this._round1(this._average(clusterEntries.map((entry) => entry.value)));
				const difference = this._round1(this._average(clusterEntries.map((entry) => entry.diffV1)));
				return {
					clusterId,
					clusterName: clusterNameById.get(clusterId) || `${t('Cluster')} ${clusterId}`,
					value,
					percentage: value,
					formattedValue: `${value}`,
					difference,
					formattedDifference: this._formatDifference(difference),
					classColor: this._differenceClassColor(difference),
					color: 'primary',
					objective: '',
					deliveryLocationCount: clusterEntries.length,
				};
			})
			.sort((first, second) => second.value - first.value);
	}

	/**
	 * Per-DL detail rows, resolving account/channel/type/cluster names the same way kpiService does so the
	 * SPS grid columns line up with the KPI detail grid.
	 * @private
	 */
	async _buildRows(entries, clusterNameById) {
		const ecosVM = iframeDataBridge.getEcosVM();
		const accountById = new Map(this._getEcosCollection(ecosVM?.accounts).map((account) => [account.id, account]));
		const channelById = new Map(this._getEcosCollection(ecosVM?.channels).map((channel) => [channel.id, channel]));
		const deliveryLocationTypeById = new Map(this._getEcosCollection(ecosVM?.deliveryLocationTypes).map((deliveryLocationType) => [deliveryLocationType.id, deliveryLocationType]));
		const organisationById = buildOrganisationById(this._getEcosCollection(ecosVM?.organisations));
		const organisationMaxLevel = await getOrganisationMaxLevel();
		const locationGroupNameById = await this._getLocationGroupNameById();

		return entries.map((entry) => {
			const deliveryLocation = entry.deliveryLocation;
			const account = accountById.get(deliveryLocation.accountId);
			const channel = channelById.get(deliveryLocation.channelId);
			const deliveryLocationType = deliveryLocationTypeById.get(deliveryLocation.deliveryLocationTypeId);
			const clusterId = Number(deliveryLocation.clusterId) || null;
			const locationGroup = locationGroupNameById.get(Number(deliveryLocation.groupId)) || '';

			const organisationIds = (Array.isArray(deliveryLocation.organisations) ? deliveryLocation.organisations : []).map(Number);
			const organisations = organisationIds.map((id) => organisationById.get(id)).filter(Boolean);
			const organisationPaths = this._buildOrganisationPaths(organisations, organisationById, organisationMaxLevel);
			const organisationLevels = organisationPaths[0] || {};
			const hasMultipleOrgPaths = organisationPaths.length > 1;

			return {
				id: deliveryLocation.id,
				deliveryLocationId: deliveryLocation.id,
				deliveryLocation: deliveryLocation.companyName || deliveryLocation.label || deliveryLocation.name || '',
				account: account?.name || account?.label || account?.reference || '',
				channel: channel?.name || channel?.label || channel?.reference || '',
				deliveryLocationType: deliveryLocationType?.name || deliveryLocationType?.label || deliveryLocationType?.reference || '',
				cluster: clusterId ? clusterNameById.get(clusterId) || `${t('Cluster')} ${clusterId}` : '',
				clusterId,
				locationGroup,
				...organisationLevels,
				_organisationPaths: hasMultipleOrgPaths ? organisationPaths : null,
				address: [deliveryLocation.addressLine, deliveryLocation.addressLine2].filter(Boolean).join(' '),
				postcode: deliveryLocation.postcode || deliveryLocation.postalCode || '',
				city: deliveryLocation.city || '',
				value: this._round1(entry.value),
				difference: this._round1(entry.diffV1),
				color: 'primary',
			};
		});
	}

	/**
	 * Raw SPS value per delivery location, for an explicit id list (no perimeter/filter logic) —
	 * used by callers that already have their own store list (e.g. the Zone de chalandise
	 * definition/comparison views) and just need each store's score. Missing/unscored DLs are
	 * simply absent from the returned map, same as _buildDeliveryLocationEntries.
	 * @param {Array<number>} deliveryLocationIds
	 * @returns {Promise<Map<number, {value: number, diffV1: number}>>}
	 */
	async getSpsByDeliveryLocationIds(deliveryLocationIds = []) {
		if (!this.isAvailable() || !deliveryLocationIds.length) return new Map();
		try {
			return await this._fetchSpsByDeliveryLocation(deliveryLocationIds);
		} catch (error) {
			log('error', 'SpsService', `Error fetching SPS by delivery location ids: ${error.message}`);
			return new Map();
		}
	}

	/**
	 * Global SPS gauge value (widget mini mode). Single bridge fetch across all filtered stores.
	 */
	async getSpsOverview(filters = {}) {
		try {
			const entries = await this._buildDeliveryLocationEntries(filters);
			return this._buildOverview(entries);
		} catch (error) {
			log('error', 'SpsService', `Error building SPS overview: ${error.message}`);
			return this._buildOverview([]);
		}
	}

	/**
	 * Full SPS report for the detail view (overview + clusters sorted high→low + per-DL rows) built from a
	 * SINGLE bridge fetch, so opening/filtering the detail doesn't hit the KPI snapshot endpoint twice.
	 */
	async getSpsReport(filters = {}) {
		try {
			const [entries, clusterNameById] = await Promise.all([this._buildDeliveryLocationEntries(filters), this._getClusterNameById()]);
			return {
				overview: this._buildOverview(entries),
				clusters: this._buildClusters(entries, clusterNameById),
				rows: await this._buildRows(entries, clusterNameById),
			};
		} catch (error) {
			log('error', 'SpsService', `Error building SPS report: ${error.message}`);
			return { overview: this._buildOverview([]), clusters: [], rows: [] };
		}
	}
}

export const spsService = new SpsService();
